<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div v-for="(person, index) in contacts" :key="index">
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("name") }}: </span
            >{{ person.name }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("email") }}: </span
            >{{ person.email }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("phone") }}: </span
            >{{ person.dialCode }} {{ person.phone }}
          </p>
        </div>
      </template>
    </ReportItemContainer>

    <div v-else>
      <p>
        {{ question.text }}
      </p>
      <div v-for="(person, index) in contacts" :key="index">
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("name") }}</p>
            <v-text-field
              v-model="person.name"
              @keyup="emitNewContacts"
              variant="outlined"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("email") }}</p>
            <v-text-field
              v-model="person.email"
              @keyup="emitNewContacts"
              variant="outlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="2">
            <p>{{ getLabel("dialCode") }}</p>
            <v-autocomplete
              :value="person.dialCode"
              :items="itemsStore.countries"
              item-title="dialCodeTitle"
              item-value="dialCode"
              return-object
              @update:model-value="(val: Country | null) => emitNewDialCode(val?.dialCode ?? '', person)"
              variant="outlined"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <p>{{ getLabel("phone") }}</p>
            <v-text-field
              v-model="person.phone"
              @keyup="emitNewContacts"
              variant="outlined"
            />
          </v-col>
        </v-row>
      </div>
      <v-row justify="center">
        <p-primary-button @click="addPerson()" class="ma-2 mb-15">
          <v-icon icon="mdi-plus"></v-icon> Add Person
        </p-primary-button>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import ContactPerson from "@/models/persons/contactPerson";
import { computed, ref } from "vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import type { Country } from "@/models/country";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { useItemsStore } from "@/stores/itemsStore";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: ContactPerson[] | null;
    isReportMode?: boolean;
  }>(),
  {
    isReportMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: ContactPerson[]): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const contactPersonDisplayTextItems = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.ContactPerson
);

const contacts = ref(getContacts());
const question = computed(() => props.questionModel?.question);
const itemsStore = useItemsStore();

function getContacts(): ContactPerson[] {
  return props.answer && props.answer.length > 0
    ? (props.answer as ContactPerson[])
    : [new ContactPerson()];
}

function emitNewContacts(): void {
  emit("qEvent", contacts.value);
}

function addPerson(): void {
  contacts.value.push(new ContactPerson());
}

function emitNewDialCode(newValue: string, person: ContactPerson): void {
  person.dialCode = newValue;
  emitNewContacts();
}

function getLabel(id: string): string {
  return contactPersonDisplayTextItems.find((x) => x.id == id)?.text ?? "";
}
</script>
